/**
 * Tab contents for UnitDetail. Each export is a presentational component:
 * it receives the unit dict and any handlers as props.
 */
import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, HelpCircle, Sparkles, Play } from 'lucide-react';

export function StandardsTab({
  unit, generatingEQ, generatingStandards,
  onGenerateEssentialQuestions, onEnhanceStandards,
}) {
  return (
    <div className="space-y-6">
      <Card className="p-8 print-friendly bg-[#FBF6E8] border-[#D4AF37]/40">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-serif font-normal text-[#14213D]">Essential Questions</h2>
          {(!unit.essential_questions || unit.essential_questions.length === 0) && (
            <Button
              size="sm" variant="outline"
              onClick={onGenerateEssentialQuestions}
              disabled={generatingEQ}
              data-testid="generate-eq-btn"
              className="no-print"
            >
              {generatingEQ ? (
                <><Loader2 className="h-3 w-3 mr-2 animate-spin" />Generating...</>
              ) : (
                <><HelpCircle className="h-3 w-3 mr-2" />Generate Questions</>
              )}
            </Button>
          )}
        </div>
        {unit.essential_questions && unit.essential_questions.length > 0 ? (
          <div className="space-y-4">
            {unit.essential_questions.map((eq, index) => (
              <div key={typeof eq === 'string' ? eq.slice(0, 50) : eq.question?.slice(0, 50) || index} className="border-l-4 border-yellow-500 pl-4 py-2">
                <p className="text-lg text-[#14213D] font-medium mb-2">{typeof eq === 'string' ? eq : eq.question}</p>
                {typeof eq !== 'string' && eq.explanation && (
                  <p className="text-sm text-[#5A6577] italic">{eq.explanation}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#5A6577]">Click &ldquo;Generate Questions&rdquo; to create essential questions for this unit using AI</p>
        )}
      </Card>

      <Card className="p-8 print-friendly">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-serif font-normal text-[#14213D]">Kansas HGSS Standards</h2>
          {(!unit.detailed_standards || unit.detailed_standards.length === 0) && (
            <Button
              size="sm" variant="outline"
              onClick={onEnhanceStandards}
              disabled={generatingStandards}
              data-testid="enhance-standards-btn"
              className="no-print"
            >
              {generatingStandards ? (
                <><Loader2 className="h-3 w-3 mr-2 animate-spin" />Enhancing...</>
              ) : (
                <><Sparkles className="h-3 w-3 mr-2" />Enhance Standards</>
              )}
            </Button>
          )}
        </div>
        {unit.detailed_standards && unit.detailed_standards.length > 0 ? (
          <div className="space-y-6">
            {unit.detailed_standards.map((standard) => (
              <div key={standard.code} className="border border-[#EBE7E0] rounded-lg p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="standards-code">{standard.code}</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#14213D] mb-2">{standard.title}</h3>
                    <p className="text-[#2D3748] mb-3">{standard.description}</p>
                    {standard.benchmarks && standard.benchmarks.length > 0 && (
                      <>
                        <p className="text-sm font-semibold text-[#14213D] mb-2">Benchmarks:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-[#2D3748]">
                          {standard.benchmarks.map((benchmark) => (
                            <li key={`bm-${benchmark.slice(0, 20)}`}>{benchmark}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : unit.kansas_standards && unit.kansas_standards.length > 0 ? (
          <div className="space-y-4">
            {unit.kansas_standards.map((standard) => (
              <div key={standard.code} className="border-l-4 border-yellow-500 pl-4 py-2">
                <div className="standards-code inline-block mb-2">{standard.code}</div>
                <p className="text-[#2D3748]">{standard.description}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#5A6577]">No standards added yet</p>
        )}
      </Card>

      <Card className="p-8 print-friendly">
        <h2 className="text-2xl font-serif font-normal text-[#14213D] mb-6">Connections to Present</h2>
        {unit.themes_to_present && unit.themes_to_present.length > 0 ? (
          <div className="space-y-6">
            {unit.themes_to_present.map((theme, index) => (
              <div key={`theme-${index}-${theme.historical_context?.slice(0, 20)}`} className="border border-[#EBE7E0] rounded-lg p-6">
                <h3 className="font-semibold text-[#14213D] mb-2">Historical Context</h3>
                <p className="text-[#2D3748] mb-4">{theme.historical_context}</p>
                <h3 className="font-semibold text-[#14213D] mb-2">Present-Day Connection</h3>
                <p className="text-[#2D3748] mb-4">{theme.present_day_connection}</p>
                {theme.discussion_points && theme.discussion_points.length > 0 && (
                  <>
                    <h3 className="font-semibold text-[#14213D] mb-2">Discussion Points</h3>
                    <ul className="list-disc list-inside space-y-1 text-[#2D3748]">
                      {theme.discussion_points.map((point) => (
                        <li key={`dp-${point.slice(0, 20)}`}>{point}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#5A6577]">No theme connections added yet</p>
        )}
      </Card>

      {unit.teacher_notes && (
        <Card className="p-8 bg-[#FBF6E8] border-[#D4AF37]/40 print-friendly">
          <h2 className="text-xl font-serif font-normal text-[#14213D] mb-4">Teacher Notes</h2>
          <p className="text-[#2D3748] whitespace-pre-wrap">{unit.teacher_notes}</p>
        </Card>
      )}

      {unit.essay_prompts && unit.essay_prompts.length > 0 && (
        <Card className="p-8 print-friendly">
          <h2 className="text-2xl font-serif font-normal text-[#14213D] mb-6">Essay Prompts</h2>
          <div className="space-y-5">
            {unit.essay_prompts.map((ep) => (
              <div key={ep.title} className="border-l-4 border-[#D4AF37] pl-5 py-2">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-[#14213D]">{ep.title}</h3>
                  {ep.type && <span className="text-xs bg-[#F9F8F4] text-[#5A6577] px-2 py-0.5 rounded capitalize">{ep.type}</span>}
                </div>
                <p className="text-[#2D3748]">{ep.prompt}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {unit.rubric && (
        <Card className="p-8 print-friendly">
          <h2 className="text-2xl font-serif font-normal text-[#14213D] mb-2">{unit.rubric.title || 'Assessment Rubric'}</h2>
          {unit.rubric.categories && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-[#F9F8F4]">
                    <th className="text-left p-3 border border-[#EBE7E0] font-semibold">Category</th>
                    <th className="text-center p-3 border border-[#EBE7E0] font-semibold text-green-800">Exemplary</th>
                    <th className="text-center p-3 border border-[#EBE7E0] font-semibold text-blue-800">Proficient</th>
                    <th className="text-center p-3 border border-[#EBE7E0] font-semibold text-[#14213D]">Developing</th>
                    <th className="text-center p-3 border border-[#EBE7E0] font-semibold text-red-800">Beginning</th>
                  </tr>
                </thead>
                <tbody>
                  {unit.rubric.categories.map((cat) => (
                    <tr key={cat.name} className="even:bg-white odd:bg-[#F9F8F4]">
                      <td className="p-3 border border-[#EBE7E0] font-medium text-[#14213D]">
                        {cat.name}
                        {cat.weight && <span className="text-xs text-[#5A6577] ml-1">({cat.weight}%)</span>}
                      </td>
                      <td className="p-3 border border-[#EBE7E0] text-xs text-[#2D3748]">{cat.levels?.exemplary}</td>
                      <td className="p-3 border border-[#EBE7E0] text-xs text-[#2D3748]">{cat.levels?.proficient}</td>
                      <td className="p-3 border border-[#EBE7E0] text-xs text-[#2D3748]">{cat.levels?.developing}</td>
                      <td className="p-3 border border-[#EBE7E0] text-xs text-[#2D3748]">{cat.levels?.beginning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

export function LessonsTab({ unit }) {
  if (!unit.lesson_plans || unit.lesson_plans.length === 0) {
    return (
      <Card className="p-8">
        <p className="text-[#5A6577] text-center">No lesson plans added yet</p>
      </Card>
    );
  }
  return (
    <div className="space-y-6">
      {unit.lesson_plans.map((lesson) => (
        <Card key={lesson.title} className="p-8 print-friendly">
          <div className="flex items-center gap-3 mb-4">
            <span className="grade-badge bg-[#14213D] text-white px-2 py-1 rounded">
              {lesson.type === 'project' ? 'Group Project' : 'Argumentative Essay'}
            </span>
            {lesson.duration && (
              <span className="text-sm text-[#5A6577]">{lesson.duration}</span>
            )}
          </div>
          <h2 className="text-2xl font-serif font-normal text-[#14213D] mb-4">{lesson.title}</h2>
          <p className="text-[#2D3748] mb-6">{lesson.description}</p>

          {lesson.objectives && lesson.objectives.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-[#14213D] mb-3">Learning Objectives</h3>
              <ul className="list-disc list-inside space-y-2 text-[#2D3748]">
                {lesson.objectives.map((obj) => <li key={`obj-${obj.slice(0, 20)}`}>{obj}</li>)}
              </ul>
            </div>
          )}

          {lesson.materials && lesson.materials.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-[#14213D] mb-3">Materials Needed</h3>
              <ul className="list-disc list-inside space-y-1 text-[#2D3748]">
                {lesson.materials.map((material) => <li key={`mat-${material.slice(0, 20)}`}>{material}</li>)}
              </ul>
            </div>
          )}

          {lesson.instructions && (
            <div className="mb-6">
              <h3 className="font-semibold text-[#14213D] mb-3">Instructions</h3>
              <p className="text-[#2D3748] whitespace-pre-wrap">{lesson.instructions}</p>
            </div>
          )}

          {lesson.assessment_criteria && lesson.assessment_criteria.length > 0 && (
            <div>
              <h3 className="font-semibold text-[#14213D] mb-3">Assessment Criteria</h3>
              <ul className="list-disc list-inside space-y-1 text-[#2D3748]">
                {lesson.assessment_criteria.map((criteria) => <li key={`crit-${criteria.slice(0, 20)}`}>{criteria}</li>)}
              </ul>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

export function VocabularyTab({ unit, isStudent }) {
  return (
    <Card className="p-8 print-friendly">
      <h2 className="text-2xl font-serif font-normal text-[#14213D] mb-6">Vocabulary List</h2>
      {unit.vocabulary && unit.vocabulary.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {unit.vocabulary.map((item) => (
            <div key={item.term} className="border-b border-[#EBE7E0] pb-4">
              <h3 className="font-mono font-semibold text-[#14213D] mb-2">{item.term}</h3>
              {!isStudent && <p className="text-[#2D3748] mb-2">{item.definition}</p>}
              {!isStudent && item.context && (
                <p className="text-sm text-[#5A6577] italic">{item.context}</p>
              )}
              {isStudent && <p className="text-[#A0AEC0] text-sm italic">Definition hidden — study mode</p>}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[#5A6577]">No vocabulary items added yet</p>
      )}
    </Card>
  );
}

export function IdentificationsTab({ unit, isStudent }) {
  return (
    <Card className="p-8 print-friendly">
      <h2 className="text-2xl font-serif font-normal text-[#14213D] mb-6">Key Identifications</h2>
      {unit.identifications && unit.identifications.length > 0 ? (
        <div className="space-y-6">
          {unit.identifications.map((item) => (
            <div key={item.name} className="border border-[#EBE7E0] rounded-lg p-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="standards-code">
                  {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                </span>
              </div>
              <h3 className="text-xl font-serif font-normal text-[#14213D] mb-2">{item.name}</h3>
              {!isStudent && <p className="text-[#2D3748] mb-3">{item.description}</p>}
              {!isStudent && (
                <div className="bg-[#F9F8F4] p-4 rounded">
                  <h4 className="font-semibold text-[#14213D] mb-2">Significance</h4>
                  <p className="text-[#2D3748]">{item.significance}</p>
                </div>
              )}
              {isStudent && <p className="text-[#A0AEC0] text-sm italic">Description hidden — study mode</p>}
              {!isStudent && item.connections && item.connections.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-semibold text-[#14213D] mb-2">Connections</h4>
                  <ul className="list-disc list-inside space-y-1 text-[#2D3748]">
                    {item.connections.map((conn) => <li key={`conn-${conn.slice(0, 20)}`}>{conn}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[#5A6577]">No identification items added yet</p>
      )}
    </Card>
  );
}

export function VideosTab({ unitVideos, playingVideoId, onTogglePlay }) {
  return (
    <Card className="p-6">
      <h3 className="font-serif text-lg text-[#14213D] mb-4">Crash Course Videos</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {unitVideos.map((v) => (
          <div
            key={v.id}
            className="rounded-lg overflow-hidden border border-[#EBE7E0] hover:shadow-md transition-shadow cursor-pointer group"
            onClick={() => onTogglePlay(v.id)}
            data-testid={`unit-video-${v.id}`}
          >
            {playingVideoId === v.id ? (
              <div className="aspect-video">
                <iframe
                  src={`https://www.youtube.com/embed/${v.youtube_id}?autoplay=1`}
                  title={v.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="relative aspect-video bg-gradient-to-br from-[#14213D] to-[#0F1A2E]">
                <img
                  src={`https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`}
                  alt={v.title}
                  className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 bg-red-600/90 rounded-full flex items-center justify-center group-hover:bg-red-600 transition-colors shadow-lg">
                    <Play className="h-5 w-5 text-white ml-0.5" fill="white" />
                  </div>
                </div>
              </div>
            )}
            <div className="p-3">
              <h4 className="text-xs font-medium text-[#14213D] line-clamp-2">{v.title}</h4>
              <p className="text-[11px] text-[#A0AEC0] mt-1">{v.series} &middot; {v.duration}</p>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}


export function GeographyTab({ unit }) {
  const gc = unit.geography_connections;
  if (!gc) {
    return (
      <Card className="p-6" data-testid="geography-tab-empty">
        <p className="text-sm text-[#5A6577]">No geography connections have been added for this unit yet.</p>
      </Card>
    );
  }
  const { physical = [], political = [], key_map_locations = [], discussion_points = [] } = gc;

  return (
    <div className="space-y-4" data-testid="geography-tab-content">
      {gc.title_context && (
        <Card className="p-5 bg-gradient-to-r from-[#F9F8F4] to-white border-[#EBE7E0]">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-1">Geography Focus</p>
          <h3 className="font-serif text-xl text-[#14213D]">{gc.title_context}</h3>
        </Card>
      )}

      {physical.length > 0 && (
        <Card className="p-5" data-testid="geo-physical-section">
          <h3 className="font-serif text-lg text-[#14213D] mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#78350F]" /> Physical Geography
          </h3>
          <div className="space-y-3">
            {physical.map((p, i) => (
              <div key={`phys-${i}`} className="border-l-2 border-[#78350F]/40 pl-3">
                <div className="flex items-baseline gap-2">
                  <span className="font-medium text-sm text-[#14213D]">{p.name}</span>
                  {p.type && <span className="text-[11px] uppercase tracking-wide text-[#A0AEC0]">{p.type}</span>}
                </div>
                <p className="text-xs text-[#4A5568] mt-1 leading-relaxed">{p.significance}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {political.length > 0 && (
        <Card className="p-5" data-testid="geo-political-section">
          <h3 className="font-serif text-lg text-[#14213D] mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#1E40AF]" /> Political Geography
          </h3>
          <div className="space-y-3">
            {political.map((p, i) => (
              <div key={`pol-${i}`} className="border-l-2 border-[#1E40AF]/40 pl-3">
                <div className="flex items-baseline gap-2">
                  <span className="font-medium text-sm text-[#14213D]">{p.name}</span>
                  {p.type && <span className="text-[11px] uppercase tracking-wide text-[#A0AEC0]">{p.type}</span>}
                </div>
                <p className="text-xs text-[#4A5568] mt-1 leading-relaxed">{p.significance}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {key_map_locations.length > 0 && (
        <Card className="p-5" data-testid="geo-locations-section">
          <h3 className="font-serif text-lg text-[#14213D] mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#B91C1C]" /> Key Locations
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {key_map_locations.map((loc, i) => (
              <div key={`loc-${i}`} className="p-3 rounded bg-[#F9F8F4] border border-[#EBE7E0]">
                <div className="flex items-baseline justify-between">
                  <span className="font-medium text-sm text-[#14213D]">{loc.name}</span>
                  <span className="text-[11px] text-[#A0AEC0] font-mono">{loc.lat?.toFixed?.(2)}, {loc.lng?.toFixed?.(2)}</span>
                </div>
                <p className="text-xs text-[#4A5568] mt-1 leading-relaxed">{loc.description}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#A0AEC0] mt-3">Explore these on the <a href="/geography" className="underline hover:text-[#14213D]">Interactive World Map</a>.</p>
        </Card>
      )}

      {discussion_points.length > 0 && (
        <Card className="p-5" data-testid="geo-discussion-section">
          <h3 className="font-serif text-lg text-[#14213D] mb-3">Discussion Prompts</h3>
          <ul className="space-y-2">
            {discussion_points.map((d, i) => (
              <li key={`disc-${i}`} className="text-sm text-[#2D3748] flex gap-2">
                <span className="text-[#D4AF37] font-semibold">{i + 1}.</span>
                <span className="leading-relaxed">{d}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
